import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Trips from "@/pages/Trips";
import TripDetail from "@/pages/TripDetail";
import CreateTrip from "@/pages/CreateTrip";
import Requests from "@/pages/Requests";
import RequestDetail from "@/pages/RequestDetail";
import CreateRequest from "@/pages/CreateRequest";
import MyCatalog from "@/pages/MyCatalog";
import JastiperFinance from "@/pages/JastiperFinance";
import OrderDetail from "@/pages/OrderDetail";
import Cart from "@/pages/Cart";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Profile from "@/pages/Profile";
import ReferralLeaderboard from "@/pages/ReferralLeaderboard";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="text-center py-20 text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/auth" state={{ from: location }} replace />;
  return children;
}

function AdminProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-center py-20 text-zinc-500">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
}

function JastiperOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-center py-20 text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  const userRoles = user.roles || [user.role];
  const activeMode = user.active_mode || user.role;
  // Allow if jastiper is in user's roles AND currently active.
  if (!userRoles.includes("jastiper") || activeMode !== "jastiper") return <Navigate to="/dashboard" replace />;
  return children;
}

function BuyerOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="text-center py-20 text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  const userRoles = user.roles || [user.role];
  const activeMode = user.active_mode || user.role;
  if (!userRoles.includes("buyer") || activeMode !== "buyer") return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role === "admin") return <Navigate to="/admin/dashboard" replace />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Layout>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/trips" element={<Trips />} />
            <Route path="/trips/new" element={<JastiperOnly><CreateTrip /></JastiperOnly>} />
            <Route path="/trips/:id" element={<TripDetail />} />
            <Route path="/requests" element={<Requests />} />
            <Route path="/requests/new" element={<BuyerOnly><CreateRequest /></BuyerOnly>} />
            <Route path="/requests/:id" element={<RequestDetail />} />
            <Route path="/catalog" element={<JastiperOnly><MyCatalog /></JastiperOnly>} />
            <Route path="/finance" element={<JastiperOnly><JastiperFinance /></JastiperOnly>} />
            <Route path="/orders/:id" element={<Protected><OrderDetail /></Protected>} />
            <Route path="/cart/:tripId" element={<BuyerOnly><Cart /></BuyerOnly>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/leaderboard" element={<ReferralLeaderboard />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminProtected><AdminDashboard /></AdminProtected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
